import { useState } from '#imports'

import {
  addServiceToCart,
  addToCart as addToCartAPI,
} from '@backmarket/http-api/src/api-specs-checkout/cart/cart'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import type { I18n } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES as SWAP_MODAL_NAMES } from '~/scopes/buyback/constants'
import { useSwapModalStore } from '~/scopes/buyback/swap/stores/useSwapModalStore'
import { useAddToCartOverlay } from '~/scopes/product-core/composables/useAddToCartOverlay/useAddToCartOverlay'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import type { SearchTrackingData } from '~/scopes/product-list/composables/useProductsSearch'

import { MODAL_NAMES as MODAL_NAMES_JP } from '../pages/Product-JP/Product-JP.constants'

import translations from './useAddToCart.translations'

type ErrorWithType = {
  type: string
}

function hasType(error: unknown): error is ErrorWithType {
  return (
    typeof error === 'object' &&
    error !== null &&
    Object.prototype.hasOwnProperty.call(error, 'type')
  )
}

function getErrorToastParams(error: unknown, i18n: I18n) {
  if (hasType(error)) {
    if (error.type === '/errors/cart/add-product-quantity') {
      return {
        title: i18n(translations.addProductQuantityErrorTitle),
        content: i18n(translations.addProductQuantityErrorDescription),
      }
    }

    if (
      error.type === '/errors/cart/add-service-to-bag/already-one-mobile-plan'
    ) {
      return {
        title: i18n(translations.alreadyOneMobilePlanErrorTitle),
        content: i18n(translations.alreadyOneMobilePlanErrorDescription),
      }
    }
  }

  return {}
}

export const useAddToCart = () => {
  const { openErrorToast } = useTheToast()
  const { trackAddToCart, trackErrors, trackSearchAnalytics } = useTracking()
  const { mobilePlan } = useUrlParams()
  const i18n = useI18n()
  const { hasBuybackOffer } = useBuybackOffer()
  const swapModalStore = useSwapModalStore()
  const experiments = useExperiments()
  const userStore = useUserStore()
  const { openAddToCartOverlay } = useAddToCartOverlay()
  const visitorId = useVisitorId()

  const addToCart = async ({
    listingId,
    listingPublicId,
    tracking,
    partnerPromoCodes,
    isTradeInEligible,
    promoCodes,
  }: {
    listingId?: number | null
    listingPublicId: string
    tracking: object
    partnerPromoCodes: GetProductResponse['includedServiceOffers']['partnerPromoCodes']
    isTradeInEligible: boolean
    promoCodes?: GetProductResponse['promoCodes']
  }) => {
    trackAddToCart({
      ...tracking,
      quantity: 1,
    })

    const searchTrackingData = useState<SearchTrackingData>('search-query')

    try {
      // This fields is optional in the new endpoint but this should no happen
      if (!listingId) {
        throw new Error()
      }

      await $httpFetch(addToCartAPI, {
        body: {
          listing_id: listingId,
          searchRelatedQuery: {
            id: searchTrackingData.value?.id ?? null,
            index: searchTrackingData.value?.index ?? null,
          },
        },
      })

      const servicesToAdd = []

      if (mobilePlan.value) {
        const addMobilePlan = $httpFetch(addServiceToCart, {
          body: {
            type: 'MOBILE_PLAN',
            listingPublicId,
            offerId: mobilePlan.value,
          },
        })

        servicesToAdd.push(addMobilePlan)
      }

      const activatedPromoCodeOffer = ['VISIBLE', 'RAKUTEN']

      const promoCodeOffer = partnerPromoCodes.find((partnerPromoCode) =>
        activatedPromoCodeOffer.includes(partnerPromoCode.partnerName),
      )

      const flashSalesPromoCode = promoCodes?.find(
        (promoCode) => !!promoCode.isFlashSale,
      )?.token

      if (
        flashSalesPromoCode &&
        experiments['experiment.flashSales'] === 'enabled' &&
        experiments['experiment.promoCodeInCart'] !== 'disabled'
      ) {
        await $httpFetch(addServiceToCart, {
          body: {
            type: 'PROMO_CODE',
            listingPublicId,
            promoCodeToken: flashSalesPromoCode,
          },
        })
      }

      if (promoCodeOffer) {
        const addPromoCode = $httpFetch(addServiceToCart, {
          body: {
            type: 'PARTNER_PROMO_CODE',
            listingPublicId,
            offerId: promoCodeOffer.id,
            partnerName: promoCodeOffer.partnerName,
          },
        })

        servicesToAdd.push(addPromoCode)
      }

      await Promise.all(servicesToAdd)

      if (
        !hasBuybackOffer.value &&
        swapModalStore.showAddToCartModal &&
        isTradeInEligible
      ) {
        openModal(SWAP_MODAL_NAMES.SWAP_ATC)
      } else {
        openModal(MODAL_NAMES_JP.ADD_TO_CART)
        openAddToCartOverlay()
      }

      if (
        searchTrackingData.value?.id &&
        searchTrackingData.value.productObjectId
      ) {
        trackSearchAnalytics({
          type: 'conversion',
          objectId: searchTrackingData.value.productObjectId,
          searchQueryID: searchTrackingData.value.id ?? '',
          tokenId: visitorId,
          index: searchTrackingData.value.index ?? '',
        })
      }
      await userStore.fetchUser()
    } catch (error) {
      trackErrors({
        zone: 'add_to_cart',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (error as any)?.payload?.detail ?? 'unknown_error',
      })

      const toastParams = getErrorToastParams(error, i18n)

      openErrorToast(toastParams)
    }
  }

  return {
    addToCart,
  }
}
